import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

/**
 * VendorFormDialog — full vendor creation form.
 * Reused across Diamonds NewPacket, PacketDetail Receive, Gold NewEntry, and VendorDetail.
 */
export function VendorFormDialog({ open, onOpenChange, companyId, onCreated }) {
  const empty = { name: '', contact_person: '', phone: '', email: '', address: '', gstin: '', notes: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(empty); /* eslint-disable-next-line */ }, [open]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Vendor name is required'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/masters/vendors', { ...form, company_id: companyId });
      toast.success(`Vendor "${data.name}" created`);
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (e) { toast.error(extractError(e, 'Vendor create failed')); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inline-vendor-dialog">
        <DialogHeader>
          <DialogTitle>New Vendor</DialogTitle>
          <DialogDescription>Capture supplier information. You can edit further details later from the Vendors page.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Name <span className="text-rose-600">*</span></Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inline-vendor-name-input" />
          </div>
          <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="inline-vendor-contact-input" /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="inline-vendor-phone-input" /></div>
          <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inline-vendor-email-input" /></div>
          <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} data-testid="inline-vendor-gstin-input" /></div>
          <div className="col-span-2"><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="luxury-button" disabled={saving || !form.name.trim()} data-testid="inline-vendor-submit-button">{saving ? 'Saving…' : 'Create Vendor'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default VendorFormDialog;
